import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { LocalDate } from "@js-joda/core";
import type { SearchableDropdownOption } from "components/designV2/searchableDropdown/searchableDropdown.types";
import { Api } from "services/api/api.service";
import type { BackendAxiosError } from "services/api/backendError";
import type {
  AddStaffWorkerTaxRequest,
  EmploymentTemplate,
  EmploymentTemplateListItem,
  EmploymentTemplateSalary,
  StaffProfileData,
  UpdateStaffWorkerSalaryRequest,
} from "services/api/company/staff.service";
import type { UnitTeamOption } from "web-apps/company/components/common/filters/unitTeamDropdown.component";
import { getStockholmLocalDateNow } from "web-apps/company/utils/timeAndDate";
import {
  filterTemplateSalariesByAge,
  toTemplateEnumOrFallback,
} from "../shared/staffEmploymentTemplate.logic";
import {
  PAYMENT_METHOD_OPTIONS,
  toPaymentMethodValidationTranslationKey,
  validatePaymentMethodValue,
} from "../shared/staff.paymentMethod.logic";
import { getAgeFromPersonalIdentityNumber } from "../shared/staffPersonalIdentityNumber.logic";
import type {
  EditingTab,
  EmploymentSaveMode,
  EmploymentDetailsForm,
  PaymentMethodDetailsForm,
  PersonalDetailsForm,
  ProfilePanelTab,
  SelectOption,
} from "./profilePanel.contract";
import { toEmploymentOptions } from "./domains/employment/profilePanel.options.logic";
import { resolveStaffProfilePanelErrorMessage } from "./domains/employment/profilePanel.error.logic";
import { toLocalDateOrNull } from "./domains/employment/profilePanel.payload.logic";
import {
  toEmploymentUiRules,
  withNormalizedEmploymentFormByRules,
} from "./domains/employment/profilePanel.rules.logic";
import { fetchEmployeeNumberForProfilePanel } from "./domains/employment/profilePanel.employeeNumber.logic";
import {
  toIsoDateStartOfDayUtc,
  toNonNegativeNumber,
} from "./domains/employment/profilePanel.payload.logic";
import {
  EMPTY_EMPLOYMENT_DETAILS,
  EMPTY_PAYMENT_METHOD_DETAILS,
  EMPTY_PERSONAL_DETAILS,
  toEmployeeNumberForNewEmployment,
  toEmploymentDetailsForm,
  toPaymentMethodDetailsForm,
  toPersonalDetailsForm,
} from "./profilePanel.state.formMappers.logic";
import { performSaveEmployment as runPerformSaveEmployment } from "./profilePanel.state.employment.logic";
import { useExecuteSavingAction } from "./profilePanel.state.saveAction.logic";
import {
  canSaveEmploymentEdit as toCanSaveEmploymentEdit,
  isValidPhoneNumber,
} from "./profilePanel.state.validation.logic";

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface CompanySettingValue {
  key: string;
  value?: string | null;
}

interface UseStaffProfilePanelStateParams {
  isOpen: boolean;
  workerId: number | null;
  companyId: number | null;
  initialTab?: ProfilePanelTab;
  profile: StaffProfileData | null;
  unitTeamOptions: UnitTeamOption[];
  subscription: string | undefined;
  companySettings: CompanySettingValue[];
  t: TranslateFn;
  reloadProfile: () => void;
  onWorkerUpdated?: () => void;
}

interface UseStaffProfilePanelStateResult {
  activeTab: ProfilePanelTab;
  editingTab: EditingTab;
  employmentSaveMode: EmploymentSaveMode;
  editedPersonalDetails: PersonalDetailsForm;
  editedEmploymentDetails: EmploymentDetailsForm;
  editedPaymentMethodDetails: PaymentMethodDetailsForm;
  paymentMethodError: string;
  canSavePaymentMethod: boolean;
  isSaving: boolean;
  isLoadingEmployeeNumber: boolean;
  employmentTemplates: EmploymentTemplateListItem[];
  isLoadingEmploymentTemplates: boolean;
  selectedEmploymentTemplateId: string;
  applyEmploymentTemplate: (templateId: string) => void;
  templateSalaries: EmploymentTemplateSalary[];
  selectedTemplateSalaryId: string;
  applyEmploymentTemplateSalary: (salaryId: string) => void;
  isSalaryModalOpen: boolean;
  isTaxModalOpen: boolean;
  isPaymentMethodModalOpen: boolean;
  isEndEmploymentModalOpen: boolean;
  isEndEmploymentForCreateModalOpen: boolean;
  salaryDraftHistory: DraftSalaryItem[];
  taxDraftHistory: DraftTaxItem[];
  effectiveFromDate: LocalDate | null;
  endEmploymentDate: LocalDate | null;
  salaryEarliestStartDate: LocalDate | null;
  saveError: string;
  showLockedEditHint: boolean;
  employmentRules: ReturnType<typeof toEmploymentUiRules>;
  employmentOptions: ReturnType<typeof toEmploymentOptions>;
  paymentMethodOptions: SelectOption[];
  teamOptions: SearchableDropdownOption[];
  isProfilePhoneNumberValid: boolean;
  canSaveEmploymentEdit: boolean;
  isDiscardConfirmOpen: boolean;
  setShowLockedEditHint: (value: boolean) => void;
  handleConfirmDiscardClose: () => void;
  handleCancelDiscardClose: () => void;
  handleTabChange: (value: string) => void;
  handleEditClick: () => void;
  handleAddEmploymentClick: () => void;
  handleCancelEdit: () => void;
  handleOpenSaveModal: () => void;
  handleOpenSalaryModal: () => void;
  handleCloseSalaryModal: () => void;
  handleConfirmSalaryModal: () => void;
  handleAddSalaryModal: () => void;
  handleDeleteSalaryModal: (salaryId: number) => void;
  handleUpdateSalaryDraftValue: (salaryId: number, value: number) => void;
  handleOpenTaxModal: () => void;
  handleCloseTaxModal: () => void;
  handleConfirmTaxModal: () => void;
  handleAddTaxModal: () => void;
  handleDeleteTaxModal: (taxId: number) => void;
  handleOpenPaymentMethodModal: () => void;
  handleClosePaymentMethodModal: () => void;
  handleConfirmPaymentMethodModal: () => void;
  handleDeletePaymentMethodModal: () => void;
  handleOpenEndEmploymentModal: () => void;
  handleCloseEndEmploymentModal: () => void;
  handleConfirmEndEmploymentModal: () => void;
  handleCloseEndEmploymentForCreateModal: () => void;
  handleConfirmEndEmploymentForCreateModal: () => void;
  fetchEmployeeNumber: () => void;
  updatePersonalField: <TField extends keyof PersonalDetailsForm>(
    field: TField,
    value: PersonalDetailsForm[TField],
  ) => void;
  updateEmploymentField: <TField extends keyof EmploymentDetailsForm>(
    field: TField,
    value: EmploymentDetailsForm[TField],
  ) => void;
  updatePaymentMethodField: <TField extends keyof PaymentMethodDetailsForm>(
    field: TField,
    value: PaymentMethodDetailsForm[TField],
  ) => void;
  setEffectiveFromDate: (date: LocalDate | null) => void;
  setEndEmploymentDate: (date: LocalDate | null) => void;
}

type ModalState =
  | { type: "none" }
  | { type: "editSalary" }
  | { type: "editTax" }
  | { type: "editPaymentMethod" }
  | { type: "endEmployment" }
  | { type: "endEmploymentForCreate" };
type DiscardChangesTarget = "salary" | "tax" | "paymentMethod" | null;

interface DraftSalaryItem {
  id: number;
  startDate: string;
  value: number | null;
}

interface DraftTaxItem {
  id: number;
  startDate: string;
  value: number | null;
}

const toPhoneDigits = (phoneNumber: string): string => {
  return phoneNumber.replace(/\D/g, "").slice(0, 15);
};

const toLatestDate = (first: LocalDate, second: LocalDate): LocalDate => {
  return first.isAfter(second) ? first : second;
};

const toDefaultSalaryChangeDate = (
  nowDate: LocalDate,
  employmentStartDate: LocalDate | null,
): LocalDate => {
  const baselineDate =
    employmentStartDate == null
      ? nowDate
      : toLatestDate(nowDate, employmentStartDate);

  if (baselineDate.dayOfMonth() === 1) {
    return baselineDate;
  }

  const nextMonthDate = baselineDate.plusMonths(1);
  return LocalDate.of(nextMonthDate.year(), nextMonthDate.monthValue(), 1);
};

const applyEmploymentTemplateToEmploymentDetails = (
  details: EmploymentDetailsForm,
  template: EmploymentTemplate,
): EmploymentDetailsForm => {
  return {
    ...details,
    employmentType: String(
      toTemplateEnumOrFallback(template.employmentType, details.employmentType),
    ),
    salaryType: String(
      toTemplateEnumOrFallback(template.salaryType, details.salaryType),
    ),
    employeeCategory: String(
      toTemplateEnumOrFallback(
        template.employeeCategory,
        details.employeeCategory,
      ),
    ),
    paymentType: String(
      toTemplateEnumOrFallback(template.paymentType, details.paymentType),
    ),
    role: template.role ?? details.role,
    salary:
      template.salaryValue == null
        ? details.salary
        : String(template.salaryValue),
    workPercentage:
      template.workPercentage == null
        ? details.workPercentage
        : String(Math.round(template.workPercentage * 100)),
    agreement: template.agreement ?? details.agreement,
    noticeDays:
      template.noticeDays == null
        ? details.noticeDays
        : String(template.noticeDays),
    vacationDays:
      template.vacationDays == null
        ? details.vacationDays
        : String(template.vacationDays),
    workplace: template.workplace ?? details.workplace,
  };
};

export const useStaffProfilePanelState = (
  params: UseStaffProfilePanelStateParams,
): UseStaffProfilePanelStateResult => {
  const {
    companyId,
    companySettings,
    initialTab,
    isOpen,
    onWorkerUpdated,
    profile,
    reloadProfile,
    subscription,
    t,
    unitTeamOptions,
    workerId,
  } = params;
  const [activeTab, setActiveTab] = useState<ProfilePanelTab>(
    initialTab ?? "profile",
  );
  const [editingTab, setEditingTab] = useState<EditingTab>(null);

  const [employmentSaveMode, setEmploymentSaveMode] =
    useState<EmploymentSaveMode>(null);
  const [editedPersonalDetails, setEditedPersonalDetails] =
    useState<PersonalDetailsForm>(EMPTY_PERSONAL_DETAILS);
  const [editedEmploymentDetails, setEditedEmploymentDetails] =
    useState<EmploymentDetailsForm>(EMPTY_EMPLOYMENT_DETAILS);
  const [editedPaymentMethodDetails, setEditedPaymentMethodDetails] =
    useState<PaymentMethodDetailsForm>(EMPTY_PAYMENT_METHOD_DETAILS);
  const [hasTouchedPaymentMethodValue, setHasTouchedPaymentMethodValue] =
    useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingEmployeeNumber, setIsLoadingEmployeeNumber] = useState(false);
  const [employmentTemplates, setEmploymentTemplates] = useState<
    EmploymentTemplateListItem[]
  >([]);
  const [isLoadingEmploymentTemplates, setIsLoadingEmploymentTemplates] =
    useState(false);
  const [selectedEmploymentTemplateId, setSelectedEmploymentTemplateId] =
    useState("");
  const [templateSalaries, setTemplateSalaries] = useState<
    EmploymentTemplateSalary[]
  >([]);
  const [selectedTemplateSalaryId, setSelectedTemplateSalaryId] = useState("");
  const [modalState, setModalState] = useState<ModalState>({ type: "none" });
  const [effectiveFromDate, setEffectiveFromDate] = useState<LocalDate | null>(
    null,
  );
  const [endEmploymentDate, setEndEmploymentDate] = useState<LocalDate | null>(
    null,
  );
  const [saveError, setSaveError] = useState("");
  const [showLockedEditHint, setShowLockedEditHint] = useState(false);
  const [salaryDraftHistory, setSalaryDraftHistory] = useState<
    DraftSalaryItem[]
  >([]);
  const [salaryInitialHistory, setSalaryInitialHistory] = useState<
    DraftSalaryItem[]
  >([]);
  const [taxDraftHistory, setTaxDraftHistory] = useState<DraftTaxItem[]>([]);
  const [taxInitialHistory, setTaxInitialHistory] = useState<DraftTaxItem[]>(
    [],
  );
  const [paymentMethodInitialDetails, setPaymentMethodInitialDetails] =
    useState<PaymentMethodDetailsForm>(EMPTY_PAYMENT_METHOD_DETAILS);
  const [discardChangesTarget, setDiscardChangesTarget] =
    useState<DiscardChangesTarget>(null);
  const [hasEndedEmploymentForCreateFlow, setHasEndedEmploymentForCreateFlow] =
    useState(false);
  const employeeNumberRequestIdRef = useRef(0);
  const employmentTemplatesRequestIdRef = useRef(0);
  const employmentTemplateDetailRequestIdRef = useRef(0);
  const tempDraftIdRef = useRef(-1);
  const employmentRules = useMemo(() => {
    return toEmploymentUiRules(
      editedEmploymentDetails.employmentType,
      editedEmploymentDetails.salaryType,
      subscription,
      companySettings,
    );
  }, [
    companySettings,
    editedEmploymentDetails.employmentType,
    editedEmploymentDetails.salaryType,
    subscription,
  ]);

  const employmentOptions = useMemo(() => {
    return toEmploymentOptions(
      t,
      employmentRules.isSimflexityEmployee,
      employmentRules.canSelectSimflexityPay,
    );
  }, [
    employmentRules.canSelectSimflexityPay,
    employmentRules.isSimflexityEmployee,
    t,
  ]);

  const paymentMethodOptions: SelectOption[] = PAYMENT_METHOD_OPTIONS.map(
    (paymentMethodOption) => ({
      value: paymentMethodOption.value,
      label: t(paymentMethodOption.label),
    }),
  );

  const teamOptions = useMemo(() => {
    return unitTeamOptions.flatMap((unit): SearchableDropdownOption[] => {
      const unitName = (unit.unitName ?? "").trim();
      return unit.teams.map((team) => {
        const teamName = (team.name ?? "").trim();
        return {
          value: String(team.id),
          label: unitName.length > 0 ? `${unitName} - ${teamName}` : teamName,
        };
      });
    });
  }, [unitTeamOptions]);

  const currentEmploymentStartDate = useMemo(() => {
    return toLocalDateOrNull(profile?.employmentInfo?.current?.startDate);
  }, [profile?.employmentInfo]);
  const currentEmploymentId = profile?.employmentInfo?.current?.id ?? null;
  const workerAge = useMemo(() => {
    return getAgeFromPersonalIdentityNumber(
      profile?.personalIdentityNumber ?? null,
    );
  }, [profile?.personalIdentityNumber]);
  const visibleTemplateSalaries = useMemo(() => {
    return filterTemplateSalariesByAge(templateSalaries, workerAge);
  }, [templateSalaries, workerAge]);

  useEffect(() => {
    const resetTab = initialTab ?? "profile";
    if (!isOpen) {
      employmentTemplatesRequestIdRef.current += 1;
      employmentTemplateDetailRequestIdRef.current += 1;
      setActiveTab(resetTab);
      setEditingTab(null);
      setEmploymentSaveMode(null);
      setEditedPersonalDetails(EMPTY_PERSONAL_DETAILS);
      setEditedEmploymentDetails(EMPTY_EMPLOYMENT_DETAILS);
      setEditedPaymentMethodDetails(EMPTY_PAYMENT_METHOD_DETAILS);
      setHasTouchedPaymentMethodValue(false);
      setIsLoadingEmployeeNumber(false);
      setEmploymentTemplates([]);
      setIsLoadingEmploymentTemplates(false);
      setSelectedEmploymentTemplateId("");
      setTemplateSalaries([]);
      setSelectedTemplateSalaryId("");
      setSaveError("");
      setShowLockedEditHint(false);
      setSalaryDraftHistory([]);
      setSalaryInitialHistory([]);
      setTaxDraftHistory([]);
      setTaxInitialHistory([]);
      setPaymentMethodInitialDetails(EMPTY_PAYMENT_METHOD_DETAILS);
      setEffectiveFromDate(null);
      setEndEmploymentDate(null);
      setDiscardChangesTarget(null);
      setHasEndedEmploymentForCreateFlow(false);
      setModalState({ type: "none" });
      return;
    }

    setActiveTab(resetTab);
    setEditingTab(null);
    setEmploymentSaveMode(null);
    setSaveError("");
    setShowLockedEditHint(false);
    setHasEndedEmploymentForCreateFlow(false);
    setHasTouchedPaymentMethodValue(false);
    setEndEmploymentDate(null);
    setModalState({ type: "none" });
  }, [initialTab, isOpen, workerId]);

  useEffect(() => {
    const isCreateEmploymentFlow =
      isOpen &&
      editingTab === "employmentSalary" &&
      employmentSaveMode === "create";

    if (!isCreateEmploymentFlow) {
      employmentTemplatesRequestIdRef.current += 1;
      employmentTemplateDetailRequestIdRef.current += 1;
      setEmploymentTemplates([]);
      setIsLoadingEmploymentTemplates(false);
      setSelectedEmploymentTemplateId("");
      setTemplateSalaries([]);
      setSelectedTemplateSalaryId("");
      return;
    }

    if (companyId == null) return;

    const requestId = employmentTemplatesRequestIdRef.current + 1;
    employmentTemplatesRequestIdRef.current = requestId;
    setEmploymentTemplates([]);
    setIsLoadingEmploymentTemplates(true);
    setSelectedEmploymentTemplateId("");
    setTemplateSalaries([]);
    setSelectedTemplateSalaryId("");

    void Api()
      .company.staff.getEmploymentTemplates(companyId)
      .then((response) => {
        if (employmentTemplatesRequestIdRef.current !== requestId) return;
        setEmploymentTemplates(response.data ?? []);
      })
      .catch(() => {
        if (employmentTemplatesRequestIdRef.current !== requestId) return;
      })
      .finally(() => {
        if (employmentTemplatesRequestIdRef.current !== requestId) return;
        setIsLoadingEmploymentTemplates(false);
      });
  }, [companyId, editingTab, employmentSaveMode, isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (editingTab === "profile") return;

    setEditedPersonalDetails((previousValue) => ({
      ...previousValue,
      teamIds:
        profile?.workGroups.map((workGroup) => String(workGroup.id)) ?? [],
    }));
  }, [editingTab, isOpen, profile?.workGroups]);

  useEffect(() => {
    if (editingTab !== "employmentSalary") return;

    const normalizedValues = withNormalizedEmploymentFormByRules(
      {
        salaryType: editedEmploymentDetails.salaryType,
        employeeCategory: editedEmploymentDetails.employeeCategory,
        paymentType: editedEmploymentDetails.paymentType,
      },
      employmentRules,
    );

    if (
      normalizedValues.salaryType === editedEmploymentDetails.salaryType &&
      normalizedValues.employeeCategory ===
        editedEmploymentDetails.employeeCategory &&
      normalizedValues.paymentType === editedEmploymentDetails.paymentType
    ) {
      return;
    }

    setEditedEmploymentDetails((previousValue) => ({
      ...previousValue,
      salaryType: normalizedValues.salaryType,
      employeeCategory: normalizedValues.employeeCategory,
      paymentType: normalizedValues.paymentType,
    }));
  }, [editedEmploymentDetails, editingTab, employmentRules]);

  const updatePersonalField = useCallback(
    <TField extends keyof PersonalDetailsForm>(
      field: TField,
      value: PersonalDetailsForm[TField],
    ): void => {
      const nextValue =
        field === "phoneNumber" && typeof value === "string"
          ? toPhoneDigits(value)
          : value;
      setEditedPersonalDetails((previousValue) => ({
        ...previousValue,
        [field]: nextValue,
      }));
      setSaveError("");
    },
    [],
  );

  const updateEmploymentField = useCallback(
    <TField extends keyof EmploymentDetailsForm>(
      field: TField,
      value: EmploymentDetailsForm[TField],
    ): void => {
      setEditedEmploymentDetails((previousValue) => ({
        ...previousValue,
        [field]: value,
      }));
    },
    [],
  );

  const updatePaymentMethodField = useCallback(
    <TField extends keyof PaymentMethodDetailsForm>(
      field: TField,
      value: PaymentMethodDetailsForm[TField],
    ): void => {
      setEditedPaymentMethodDetails((previousValue) => ({
        ...previousValue,
        [field]: value,
      }));
      if (field === "value") {
        setHasTouchedPaymentMethodValue(true);
      }
      setSaveError("");
    },
    [],
  );

  const applyEmploymentTemplate = useCallback(
    (templateId: string): void => {
      setSelectedEmploymentTemplateId(templateId);
      setSelectedTemplateSalaryId("");
      setTemplateSalaries([]);
      setSaveError("");

      if (companyId == null || templateId.trim().length === 0) return;

      const requestId = employmentTemplateDetailRequestIdRef.current + 1;
      employmentTemplateDetailRequestIdRef.current = requestId;

      void Api()
        .company.staff.getEmploymentTemplate(companyId, Number(templateId))
        .then((response) => {
          if (employmentTemplateDetailRequestIdRef.current !== requestId) {
            return;
          }
          const template = response.data;
          if (template == null) return;

          setEditedEmploymentDetails((previousValue) =>
            applyEmploymentTemplateToEmploymentDetails(previousValue, template),
          );
          setTemplateSalaries(template.salaries ?? []);
        })
        .catch(() => {
          if (employmentTemplateDetailRequestIdRef.current !== requestId) {
            return;
          }
        });
    },
    [companyId],
  );

  const applyEmploymentTemplateSalary = useCallback(
    (salaryId: string): void => {
      setSelectedTemplateSalaryId(salaryId);
      const selectedSalary = visibleTemplateSalaries.find(
        (salary) => String(salary.id) === salaryId,
      );
      if (selectedSalary == null) return;

      setEditedEmploymentDetails((previousValue) => ({
        ...previousValue,
        salary: String(selectedSalary.value),
      }));
      setSaveError("");
    },
    [visibleTemplateSalaries],
  );

  const fetchEmployeeNumber = useCallback((): void => {
    fetchEmployeeNumberForProfilePanel({
      companyId,
      requestIdRef: employeeNumberRequestIdRef,
      setIsLoadingEmployeeNumber,
      setEditedEmploymentDetails,
    });
  }, [companyId]);

  const handleTabChange = useCallback((value: string): void => {
    if (
      value === "profile" ||
      value === "employmentSalary" ||
      value === "history"
    ) {
      setSaveError("");
      setActiveTab(value);
    }
  }, []);

  const handleEditClick = useCallback((): void => {
    if (activeTab === "profile") {
      setEditedPersonalDetails(toPersonalDetailsForm(profile));
      setEditingTab("profile");
      return;
    }

    if (activeTab === "history") {
      return;
    }

    setEditedEmploymentDetails(toEmploymentDetailsForm(profile));
    setEmploymentSaveMode("update");
    setEditingTab("employmentSalary");
  }, [activeTab, profile]);

  const handleAddEmploymentClick = useCallback((): void => {
    if (activeTab !== "employmentSalary") return;
    const hasActiveEmployment =
      profile?.employments.some((employment) => employment.isActive) === true;
    if (hasActiveEmployment && !hasEndedEmploymentForCreateFlow) {
      const today = getStockholmLocalDateNow();
      const suggestedDate =
        currentEmploymentStartDate != null &&
        today.isBefore(currentEmploymentStartDate)
          ? currentEmploymentStartDate
          : today;
      setEndEmploymentDate(suggestedDate);
      setSaveError("");
      setModalState({ type: "endEmploymentForCreate" });
      return;
    }

    const employmentDetails = toEmploymentDetailsForm(profile);
    setEditedEmploymentDetails({
      ...employmentDetails,
      employeeNumber: toEmployeeNumberForNewEmployment(profile),
    });
    setEmploymentSaveMode("create");
    setEditingTab("employmentSalary");
    setSaveError("");
  }, [
    activeTab,
    currentEmploymentStartDate,
    hasEndedEmploymentForCreateFlow,
    profile,
  ]);

  const handleCancelEdit = useCallback((): void => {
    employeeNumberRequestIdRef.current += 1;
    employmentTemplatesRequestIdRef.current += 1;
    employmentTemplateDetailRequestIdRef.current += 1;
    setIsLoadingEmployeeNumber(false);
    setEmploymentTemplates([]);
    setIsLoadingEmploymentTemplates(false);
    setSelectedEmploymentTemplateId("");
    setTemplateSalaries([]);
    setSelectedTemplateSalaryId("");
    setEditingTab(null);
    setEmploymentSaveMode(null);
    setModalState({ type: "none" });
    setSaveError("");
  }, []);

  const handleOpenSalaryModal = useCallback((): void => {
    const currentEmployment = profile?.employmentInfo?.current;
    const nextHistory =
      currentEmployment?.salaries.map((salary) => ({
        id: salary.id,
        startDate: salary.startDate ?? "",
        value: salary.value,
      })) ?? [];
    setEditedEmploymentDetails(toEmploymentDetailsForm(profile));
    setSalaryDraftHistory(nextHistory);
    setSalaryInitialHistory(nextHistory);
    setEffectiveFromDate(
      toDefaultSalaryChangeDate(
        getStockholmLocalDateNow(),
        currentEmploymentStartDate,
      ),
    );
    setSaveError("");
    setModalState({ type: "editSalary" });
  }, [currentEmploymentStartDate, profile]);

  const handleCloseSalaryModal = useCallback((): void => {
    if (isSaving) return;
    const isDirty =
      JSON.stringify(salaryDraftHistory) !==
      JSON.stringify(salaryInitialHistory);
    if (isDirty) {
      setDiscardChangesTarget("salary");
      return;
    }
    setModalState({ type: "none" });
  }, [isSaving, salaryDraftHistory, salaryInitialHistory]);

  const handleOpenTaxModal = useCallback((): void => {
    const nextHistory =
      profile?.taxes.map((tax) => ({
        id: tax.id,
        startDate: tax.startDate ?? "",
        value: tax.value,
      })) ?? [];
    setEditedEmploymentDetails(toEmploymentDetailsForm(profile));
    setTaxDraftHistory(nextHistory);
    setTaxInitialHistory(nextHistory);
    setEffectiveFromDate(
      toDefaultSalaryChangeDate(
        getStockholmLocalDateNow(),
        currentEmploymentStartDate,
      ),
    );
    setSaveError("");
    setModalState({ type: "editTax" });
  }, [currentEmploymentStartDate, profile]);

  const handleCloseTaxModal = useCallback((): void => {
    if (isSaving) return;
    const isDirty =
      JSON.stringify(taxDraftHistory) !== JSON.stringify(taxInitialHistory);
    if (isDirty) {
      setDiscardChangesTarget("tax");
      return;
    }
    setSaveError("");
    setModalState({ type: "none" });
  }, [isSaving, taxDraftHistory, taxInitialHistory]);

  const handleOpenPaymentMethodModal = useCallback((): void => {
    const nextPaymentMethodDetails = toPaymentMethodDetailsForm(profile);
    setEditedPaymentMethodDetails(nextPaymentMethodDetails);
    setPaymentMethodInitialDetails(nextPaymentMethodDetails);
    setHasTouchedPaymentMethodValue(false);
    setSaveError("");
    setModalState({ type: "editPaymentMethod" });
  }, [profile]);

  const handleClosePaymentMethodModal = useCallback((): void => {
    if (isSaving) return;
    const isDirty =
      JSON.stringify(editedPaymentMethodDetails) !==
      JSON.stringify(paymentMethodInitialDetails);
    if (isDirty) {
      setDiscardChangesTarget("paymentMethod");
      return;
    }
    setSaveError("");
    setModalState({ type: "none" });
  }, [editedPaymentMethodDetails, isSaving, paymentMethodInitialDetails]);

  const handleConfirmDiscardClose = useCallback((): void => {
    if (discardChangesTarget === "salary") {
      setModalState({ type: "none" });
    } else if (discardChangesTarget === "tax") {
      setSaveError("");
      setModalState({ type: "none" });
    } else if (discardChangesTarget === "paymentMethod") {
      setSaveError("");
      setModalState({ type: "none" });
    }
    setDiscardChangesTarget(null);
  }, [discardChangesTarget]);

  const handleCancelDiscardClose = useCallback((): void => {
    setDiscardChangesTarget(null);
  }, []);

  const executeSavingAction = useExecuteSavingAction({
    resolveErrorMessage: (error: BackendAxiosError): string =>
      resolveStaffProfilePanelErrorMessage(error, t),
    setIsSaving,
    setSaveError,
  });

  const confirmEndEmploymentUpdate = useCallback(
    (onSuccess: () => void): void => {
      if (isSaving) return;
      if (endEmploymentDate == null) {
        setSaveError(t("Alerts.SomethingWentWrong"));
        return;
      }

      const currentEmployment = profile?.employmentInfo?.current;
      const startDate = toLocalDateOrNull(currentEmployment?.startDate);

      runPerformSaveEmployment({
        companyId,
        workerId,
        profile,
        editedEmploymentDetails: toEmploymentDetailsForm(profile),
        employmentSaveMode: "update",
        editingTab: "employmentSalary",
        employmentRules,
        startDate,
        endDate: endEmploymentDate,
        translate: t,
        setIsSaving,
        setSaveError,
        setEditingTab: (): void => undefined,
        setEmploymentSaveMode: (): void => undefined,
        setModalState: (): void => onSuccess(),
        reloadProfile,
        onWorkerUpdated,
      });
    },
    [
      companyId,
      employmentRules,
      endEmploymentDate,
      isSaving,
      onWorkerUpdated,
      profile,
      reloadProfile,
      t,
      workerId,
    ],
  );

  const performSaveEmployment = useCallback(
    (startDate: LocalDate | null, endDate: LocalDate | null): void => {
      runPerformSaveEmployment({
        companyId,
        workerId,
        profile,
        editedEmploymentDetails,
        employmentSaveMode,
        editingTab,
        employmentRules,
        startDate,
        endDate,
        translate: t,
        setIsSaving,
        setSaveError,
        setEditingTab: (): void => setEditingTab(null),
        setEmploymentSaveMode: (): void => setEmploymentSaveMode(null),
        setModalState: (): void => setModalState({ type: "none" }),
        reloadProfile,
        onWorkerUpdated,
      });
    },
    [
      companyId,
      editedEmploymentDetails,
      editingTab,
      employmentSaveMode,
      employmentRules,
      onWorkerUpdated,
      profile,
      reloadProfile,
      t,
      workerId,
    ],
  );

  const handleOpenSaveModal = useCallback((): void => {
    if (editingTab === "profile") {
      if (profile?.isRegistered !== false) return;
      if (companyId == null || workerId == null) return;
      if (!isValidPhoneNumber(editedPersonalDetails.phoneNumber)) {
        setSaveError(t("Staff.AddStaffPanelPhoneNumberInvalid"));
        return;
      }

      setIsSaving(true);
      setSaveError("");

      Api()
        .company.staff.updateWorkerProfile(companyId, workerId, {
          firstName: editedPersonalDetails.firstName,
          lastName: editedPersonalDetails.lastName,
          phoneNumber: editedPersonalDetails.phoneNumber,
          email: editedPersonalDetails.email,
          personalIdentityNumber: editedPersonalDetails.personalIdentityNumber,
        })
        .then(() => {
          setEditingTab(null);
          setModalState({ type: "none" });
          reloadProfile();
          onWorkerUpdated?.();
        })
        .catch((error: BackendAxiosError) => {
          setSaveError(resolveStaffProfilePanelErrorMessage(error, t));
        })
        .finally(() => {
          setIsSaving(false);
        });
      return;
    }

    if (editingTab !== "employmentSalary") return;

    const currentEmployment = profile?.employmentInfo?.current;
    const nextStartDate =
      editedEmploymentDetails.startDate ??
      toLocalDateOrNull(currentEmployment?.startDate);
    const nextEndDate = editedEmploymentDetails.endDate;

    performSaveEmployment(nextStartDate, nextEndDate);
  }, [
    companyId,
    editedEmploymentDetails.endDate,
    editedEmploymentDetails.startDate,
    editedPersonalDetails.email,
    editedPersonalDetails.firstName,
    editedPersonalDetails.lastName,
    editedPersonalDetails.personalIdentityNumber,
    editedPersonalDetails.phoneNumber,
    editingTab,
    onWorkerUpdated,
    performSaveEmployment,
    profile,
    reloadProfile,
    t,
    workerId,
  ]);

  const handleAddSalaryModal = useCallback((): void => {
    if (effectiveFromDate == null) {
      setSaveError(t("Alerts.SomethingWentWrong"));
      return;
    }
    const nextItem: DraftSalaryItem = {
      id: tempDraftIdRef.current--,
      startDate: toIsoDateStartOfDayUtc(effectiveFromDate),
      value: toNonNegativeNumber(editedEmploymentDetails.salary),
    };
    setSalaryDraftHistory((previousValue) => [...previousValue, nextItem]);
    setSaveError("");
  }, [editedEmploymentDetails.salary, effectiveFromDate, t]);

  const handleDeleteSalaryModal = useCallback((salaryId: number): void => {
    setSalaryDraftHistory((previousValue) =>
      previousValue.filter((salary) => salary.id !== salaryId),
    );
    setSaveError("");
  }, []);

  const handleUpdateSalaryDraftValue = useCallback(
    (salaryId: number, value: number): void => {
      setSalaryDraftHistory((previousValue) =>
        previousValue.map((salary) =>
          salary.id === salaryId ? { ...salary, value } : salary,
        ),
      );
      setSaveError("");
    },
    [],
  );

  const handleConfirmSalaryModal = useCallback((): void => {
    if (isSaving) return;
    if (companyId == null || workerId == null || currentEmploymentId == null) {
      setSaveError(t("Alerts.SomethingWentWrong"));
      return;
    }
    const initialExistingIds = new Set(
      salaryInitialHistory
        .filter((salary) => salary.id > 0)
        .map((salary) => salary.id),
    );
    const draftExistingIds = new Set(
      salaryDraftHistory
        .filter((salary) => salary.id > 0)
        .map((salary) => salary.id),
    );
    const deletedIds = Array.from(initialExistingIds).filter(
      (id) => !draftExistingIds.has(id),
    );
    const addedItems = salaryDraftHistory.filter((salary) => salary.id < 0);
    const modifiedItems = salaryDraftHistory.filter((salary) => {
      if (salary.id <= 0) return false;
      const initialSalary = salaryInitialHistory.find(
        (initialItem) => initialItem.id === salary.id,
      );
      if (initialSalary == null) return false;
      return (
        initialSalary.startDate !== salary.startDate ||
        initialSalary.value !== salary.value
      );
    });
    const hasChanges =
      deletedIds.length > 0 ||
      addedItems.length > 0 ||
      modifiedItems.length > 0;
    if (!hasChanges) {
      setModalState({ type: "none" });
      return;
    }
    executeSavingAction(async () => {
      for (const salaryId of deletedIds) {
        await Api().company.staff.deleteWorkerSalary(
          companyId,
          workerId,
          currentEmploymentId,
          salaryId,
        );
      }
      for (const salary of addedItems) {
        const payload: UpdateStaffWorkerSalaryRequest = {
          salaryId: 0,
          startDate: salary.startDate,
          value: salary.value ?? 0,
        };
        await Api().company.staff.addWorkerSalary(
          companyId,
          workerId,
          payload,
          currentEmploymentId,
        );
      }
      for (const salary of modifiedItems) {
        await Api().company.staff.updateWorkerSalary(
          companyId,
          workerId,
          currentEmploymentId,
          salary.id,
          {
            startDate: salary.startDate,
            value: salary.value ?? 0,
          },
        );
      }
      setModalState({ type: "none" });
      reloadProfile();
      onWorkerUpdated?.();
    });
  }, [
    companyId,
    currentEmploymentId,
    executeSavingAction,
    isSaving,
    onWorkerUpdated,
    reloadProfile,
    t,
    workerId,
    salaryDraftHistory,
    salaryInitialHistory,
  ]);

  const handleAddTaxModal = useCallback((): void => {
    if (effectiveFromDate == null) {
      setSaveError(t("Alerts.SomethingWentWrong"));
      return;
    }
    if (
      currentEmploymentStartDate != null &&
      effectiveFromDate.isBefore(currentEmploymentStartDate)
    ) {
      setSaveError(t("Staff.ProfilePanelTaxDateBeforeEmploymentStart"));
      return;
    }
    if (effectiveFromDate.dayOfMonth() !== 1) {
      setSaveError(t("Staff.ProfilePanelTaxDateMustBeFirstOfMonth"));
      return;
    }
    const nextItem: DraftTaxItem = {
      id: tempDraftIdRef.current--,
      startDate: toIsoDateStartOfDayUtc(effectiveFromDate),
      value: toNonNegativeNumber(editedEmploymentDetails.taxTable),
    };
    setTaxDraftHistory((previousValue) => [...previousValue, nextItem]);
    setSaveError("");
  }, [
    currentEmploymentStartDate,
    editedEmploymentDetails.taxTable,
    effectiveFromDate,
    t,
  ]);

  const handleConfirmTaxModal = useCallback((): void => {
    if (isSaving) return;
    if (companyId == null || workerId == null) {
      setSaveError(t("Alerts.SomethingWentWrong"));
      return;
    }
    const initialExistingIds = new Set(
      taxInitialHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
    );
    const draftExistingIds = new Set(
      taxDraftHistory.filter((tax) => tax.id > 0).map((tax) => tax.id),
    );
    const deletedIds = Array.from(initialExistingIds).filter(
      (id) => !draftExistingIds.has(id),
    );
    const addedItems = taxDraftHistory.filter((tax) => tax.id < 0);
    const hasChanges = deletedIds.length > 0 || addedItems.length > 0;
    if (!hasChanges) {
      setSaveError("");
      setModalState({ type: "none" });
      return;
    }
    executeSavingAction(async () => {
      for (const taxId of deletedIds) {
        await Api().company.staff.deleteWorkerTax(companyId, workerId, taxId);
      }
      const activeTax =
        profile?.taxes.find((tax) => tax.isActive) ?? profile?.taxes[0] ?? null;
      for (const tax of addedItems) {
        const payload: AddStaffWorkerTaxRequest = {
          startDate: tax.startDate,
          taxType: activeTax?.type ?? "Undefined",
          value: tax.value ?? 0,
        };
        await Api().company.staff.addWorkerTax(companyId, workerId, payload);
      }
      setSaveError("");
      setModalState({ type: "none" });
      reloadProfile();
      onWorkerUpdated?.();
    });
  }, [
    companyId,
    executeSavingAction,
    isSaving,
    onWorkerUpdated,
    profile?.taxes,
    reloadProfile,
    t,
    workerId,
    taxDraftHistory,
    taxInitialHistory,
  ]);

  const handleDeleteTaxModal = useCallback((taxId: number): void => {
    setTaxDraftHistory((previousValue) =>
      previousValue.filter((tax) => tax.id !== taxId),
    );
    setSaveError("");
  }, []);

  const handleConfirmPaymentMethodModal = useCallback((): void => {
    if (isSaving) return;
    if (companyId == null || workerId == null) {
      setSaveError(t("Alerts.SomethingWentWrong"));
      return;
    }

    const validationResult = validatePaymentMethodValue(
      editedPaymentMethodDetails.type,
      editedPaymentMethodDetails.value,
    );

    if (validationResult.type === "failure") {
      setSaveError(
        t(toPaymentMethodValidationTranslationKey(validationResult.error)),
      );
      return;
    }

    const paymentMethod = profile?.paymentMethods[0] ?? null;
    executeSavingAction(async () => {
      if (paymentMethod == null) {
        await Api().company.staff.addWorkerPaymentMethod(companyId, workerId, {
          type: editedPaymentMethodDetails.type,
          value: validationResult.data,
        });
      } else {
        await Api().company.staff.updateWorkerPaymentMethod(
          companyId,
          workerId,
          paymentMethod.id,
          {
            type: editedPaymentMethodDetails.type,
            value: validationResult.data,
          },
        );
      }
      setSaveError("");
      setModalState({ type: "none" });
      reloadProfile();
      onWorkerUpdated?.();
    });
  }, [
    companyId,
    editedPaymentMethodDetails.type,
    editedPaymentMethodDetails.value,
    executeSavingAction,
    isSaving,
    onWorkerUpdated,
    profile,
    reloadProfile,
    t,
    workerId,
  ]);

  const handleDeletePaymentMethodModal = useCallback((): void => {
    if (isSaving) return;
    const paymentMethod = profile?.paymentMethods[0] ?? null;
    if (companyId == null || workerId == null || paymentMethod == null) {
      setSaveError(t("Alerts.SomethingWentWrong"));
      return;
    }

    executeSavingAction(async () => {
      await Api().company.staff.deleteWorkerPaymentMethod(
        companyId,
        workerId,
        paymentMethod.id,
      );
      setSaveError("");
      setModalState({ type: "none" });
      reloadProfile();
      onWorkerUpdated?.();
    });
  }, [
    companyId,
    executeSavingAction,
    isSaving,
    onWorkerUpdated,
    profile,
    reloadProfile,
    t,
    workerId,
  ]);

  const handleOpenEndEmploymentModal = useCallback((): void => {
    if (isSaving) return;
    const today = getStockholmLocalDateNow();
    const suggestedDate =
      currentEmploymentStartDate != null &&
      today.isBefore(currentEmploymentStartDate)
        ? currentEmploymentStartDate
        : today;
    setEndEmploymentDate(suggestedDate);
    setSaveError("");
    setModalState({ type: "endEmployment" });
  }, [currentEmploymentStartDate, isSaving]);

  const handleCloseEndEmploymentModal = useCallback((): void => {
    if (isSaving) return;
    setSaveError("");
    setModalState({ type: "none" });
  }, [isSaving]);

  const handleConfirmEndEmploymentModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      setModalState({ type: "none" });
    });
  }, [confirmEndEmploymentUpdate]);

  const handleCloseEndEmploymentForCreateModal = useCallback((): void => {
    if (isSaving) return;
    setSaveError("");
    setModalState({ type: "none" });
  }, [isSaving]);

  const handleConfirmEndEmploymentForCreateModal = useCallback((): void => {
    confirmEndEmploymentUpdate((): void => {
      setModalState({ type: "none" });
      const employmentDetails = toEmploymentDetailsForm(profile);
      setEditedEmploymentDetails({
        ...employmentDetails,
        employeeNumber: toEmployeeNumberForNewEmployment(profile),
      });
      setEmploymentSaveMode("create");
      setEditingTab("employmentSalary");
      setSaveError("");
      setHasEndedEmploymentForCreateFlow(true);
    });
  }, [confirmEndEmploymentUpdate, profile]);

  const isSalaryModalOpen = modalState.type === "editSalary";
  const isTaxModalOpen = modalState.type === "editTax";
  const isPaymentMethodModalOpen = modalState.type === "editPaymentMethod";
  const isEndEmploymentModalOpen = modalState.type === "endEmployment";
  const isEndEmploymentForCreateModalOpen =
    modalState.type === "endEmploymentForCreate";
  const isProfilePhoneNumberValid = isValidPhoneNumber(
    editedPersonalDetails.phoneNumber,
  );
  const canSaveEmploymentEdit = useMemo((): boolean => {
    return toCanSaveEmploymentEdit({
      editingTab,
      editedEmploymentDetails,
      isEndDateRequiredOnSave: employmentRules.isEndDateRequiredOnSave,
      employmentSaveMode,
    });
  }, [
    editedEmploymentDetails,
    editingTab,
    employmentRules.isEndDateRequiredOnSave,
    employmentSaveMode,
  ]);

  const paymentMethodValidationResult = useMemo(() => {
    return validatePaymentMethodValue(
      editedPaymentMethodDetails.type,
      editedPaymentMethodDetails.value,
    );
  }, [editedPaymentMethodDetails.type, editedPaymentMethodDetails.value]);

  const paymentMethodError = useMemo((): string => {
    if (paymentMethodValidationResult.type === "success") return "";
    if (
      editedPaymentMethodDetails.value.trim().length === 0 &&
      !hasTouchedPaymentMethodValue
    ) {
      return "";
    }
    return t(
      toPaymentMethodValidationTranslationKey(
        paymentMethodValidationResult.error,
      ),
    );
  }, [
    editedPaymentMethodDetails.value,
    hasTouchedPaymentMethodValue,
    paymentMethodValidationResult,
    t,
  ]);

  const canSavePaymentMethod = paymentMethodValidationResult.type === "success";

  return {
    activeTab,
    editingTab,
    employmentSaveMode,
    editedPersonalDetails,
    editedEmploymentDetails,
    editedPaymentMethodDetails,
    paymentMethodError,
    canSavePaymentMethod,
    isSaving,
    isLoadingEmployeeNumber,
    employmentTemplates,
    isLoadingEmploymentTemplates,
    selectedEmploymentTemplateId,
    applyEmploymentTemplate,
    templateSalaries: visibleTemplateSalaries,
    selectedTemplateSalaryId,
    applyEmploymentTemplateSalary,
    isSalaryModalOpen,
    isTaxModalOpen,
    isPaymentMethodModalOpen,
    isEndEmploymentModalOpen,
    isEndEmploymentForCreateModalOpen,
    salaryDraftHistory,
    taxDraftHistory,
    effectiveFromDate,
    endEmploymentDate,
    salaryEarliestStartDate: currentEmploymentStartDate,
    saveError,
    showLockedEditHint,
    employmentRules,
    employmentOptions,
    paymentMethodOptions,
    teamOptions,
    isProfilePhoneNumberValid,
    canSaveEmploymentEdit,
    isDiscardConfirmOpen: discardChangesTarget != null,
    setShowLockedEditHint,
    handleConfirmDiscardClose,
    handleCancelDiscardClose,
    handleTabChange,
    handleEditClick,
    handleAddEmploymentClick,
    handleCancelEdit,
    handleOpenSaveModal,
    handleOpenSalaryModal,
    handleCloseSalaryModal,
    handleConfirmSalaryModal,
    handleAddSalaryModal,
    handleDeleteSalaryModal,
    handleUpdateSalaryDraftValue,
    handleOpenTaxModal,
    handleCloseTaxModal,
    handleConfirmTaxModal,
    handleAddTaxModal,
    handleDeleteTaxModal,
    handleOpenPaymentMethodModal,
    handleClosePaymentMethodModal,
    handleConfirmPaymentMethodModal,
    handleDeletePaymentMethodModal,
    handleOpenEndEmploymentModal,
    handleCloseEndEmploymentModal,
    handleConfirmEndEmploymentModal,
    handleCloseEndEmploymentForCreateModal,
    handleConfirmEndEmploymentForCreateModal,
    fetchEmployeeNumber,
    updatePersonalField,
    updateEmploymentField,
    updatePaymentMethodField,
    setEffectiveFromDate,
    setEndEmploymentDate,
  };
};
